<template>
  <section v-if="getIsMaster || getIsSuperUser">
    <h1>Nominees</h1>
    <div class="my-3">
      <v-layout row wrap>
        <v-flex xs4 fluid align-center justify-center>
          <v-btn color="primary" @click="showEditModal" v-if="isWriteAvailable">
            Add nominee
          </v-btn>
          <v-btn color="primary" @click="reset">
            Reset
          </v-btn>
          <v-btn color="success" @click="exportNomineesExcel">
            xls
          </v-btn>
        </v-flex>
        <v-flex xs1>
          <v-select
            :items="categories"
            v-model="search.type"
            label="Type"
          />
        </v-flex>
        <v-flex xs2>
          <v-select
            :items="getNominationsCategories"
            v-model="search.categoryId"
            item-value="id"
            item-text="title"
            label="Categories"
          />
        </v-flex>
        <v-flex xs2 offset-xs1>
          <v-text-field
            append-icon="search"
            label="Search by user email"
            v-model="search.metadata.email"
          />
        </v-flex>
        <v-flex xs2 offset-xs1>
          <v-text-field
            append-icon="search"
            label="Search by name"
            v-model="search.name"
          ></v-text-field>
        </v-flex>
      </v-layout>
      <v-layout row wrap>
        <v-flex
          xs6 fluid
          align-center
          justify-center
        >
          <v-dialog
            v-model="isModalOpen"
            max-width="800px"
            persistent
          >
            <v-card v-if="isModalOpen">
              <v-card-title>
              </v-card-title>
              <v-card-text>
              <nominee-form
                ref="nomineeForm"
                :editableItem="editableItem"
              />
              </v-card-text>
              <v-card-actions>
                <v-spacer/>
                <v-btn color="blue darken-1" flat @click.stop="closeModal">Cancel</v-btn>
                <v-btn color="blue darken-1" flat @click.stop="saveNominee">Save</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-flex>
      </v-layout>
      <v-data-table
        :headers="headers"
        :items="nomineesList"
        class="elevation-1 wrap"
        :pagination.sync="pagination"
        :total-items="totalItemsInTable"
        :loading="loading"
        item-key="id"
        disable-initial-sort
      >
        <template slot="items" slot-scope="props">
          <td class="py-4">
            <v-avatar>
              <img
                :src="(props.item.logoUrl) || 'http://via.placeholder.com/350x200.jpg'"
                alt="no image"
              >
            </v-avatar>
          </td>
          <td class="py-4">{{ props.item.name }}</td>
          <td class="py-4">{{ props.item.awardsType }}</td>
          <td class="py-4">
            <ul>
              <li
                v-for="category in receiveNomineeCategories(props.item.categoriesIds)"
                :key="`category-${category.id}`"
              >
                {{category.title}}
              </li>
            </ul>
          </td>
          <td class="py-4">{{ formatDate(props.item.sentDate) }}</td>
          <td class="py-4">{{ formatDate(props.item.updateDate) }}</td>
          <td class="py-4">{{ props.item.metadata.email }}</td>
          <td class="py-4">
            {{ props.item.votingStep }}
          </td>
          <td class="py-4">
            {{ props.item.status }}
          </td>
          <td class="text-xs-center">
            <v-tooltip bottom>
              <v-btn
                v-if="checkIfDraftStatus(props.item.status)"
                color="green lighten-1"
                fab flat
                slot="activator"
                @click.stop="setApprovedStatus(props.item.id)"
              >
                <v-icon>thumb_up</v-icon>
              </v-btn>
              <span>Approve</span>
            </v-tooltip>
          </td>
          <td class="text-xs-center">
            <v-tooltip bottom>
              <v-btn
                color="lime"
                fab flat
                slot="activator"
                @click.stop="showEditModal(props.item)"
                :disabled="!isWriteAvailable"
              >
                <v-icon>create</v-icon>
              </v-btn>
              <span>Edit</span>
            </v-tooltip>
          </td>
          <td class="text-xs-center">
            <v-tooltip bottom>
              <v-btn
                color="error"
                fab flat
                slot="activator"
                @click.stop="removeNomineeItem(props.item.id)"
                :disabled="!isWriteAvailable"
              >
                <v-icon>delete</v-icon>
              </v-btn>
              <span>Delete</span>
            </v-tooltip>
          </td>
        </template>
        <template slot="pageText" slot-scope="{}">
          Page {{pagination.page}} of {{totalNomineesPages}}
        </template>
      </v-data-table>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import nomineeForm from './nomineeForm'
import debounce from 'lodash.debounce'
import { timeSettings } from '@/modules/settings/'
import { equals, clone } from 'ramda'
import mixins from '@/utils/mixins'
import moment from 'moment'

export default {
  name: 'Nominees',
  mixins: [mixins.isWriteAvailable],
  components: {
    nomineeForm
  },
  data () {
    return {
      headers: [
        { text: 'Icon', value: 'icon', search: false, sortable: false },
        { text: 'Name', value: 'name', search: false, sortable: true },
        { text: 'Type', value: 'awardsType', search: false, sortable: false },
        { text: 'Categories', value: 'category', search: false, sortable: false },
        { text: 'Date sent', value: 'sentDate', search: false, sortable: true },
        { text: 'Last update', value: 'updateDate', search: false, sortable: true },
        { text: 'User sent', search: false, sortable: false, align: 'center' },
        { text: 'Voting step', value: 'votingStep', search: false, sortable: true },
        { text: 'Status', value: 'status', search: false, sortable: true },
        { text: 'Approve', search: false, sortable: false, align: 'center' },
        { text: 'Edit', search: false, sortable: false, align: 'center' },
        { text: 'Delete', search: false, sortable: false, align: 'center' }
      ],
      loading: true,
      isModalOpen: false,
      editableItem: null,
      isValidationError: false,
      categories: ['Company', 'Individual'],
      search: {
        type: '',
        name: '',
        categoryId: '',
        metadata: { email: '' }
      },
      allCategories: [],
      pagination: {
        rowsPerPage: 5
      },
      // DON'T DELETE! need for isWriteAvailable mixin
      scopeForWrite: 'awards:write',
      comparedParams: null,
      isForceRequest: false
    }
  },
  computed: {
    ...mapGetters([
      'nomineesList',
      'totalNomineesPages',
      'totalNomineesItems',
      'getNominationsCategories',
      'getIsMaster',
      'getIsSuperUser'
    ]),
    totalItemsInTable () {
      return this.getNominationsTotalItems || (this.totalNomineesPages * this.pagination.rowsPerPage)
    }
  },
  watch: {
    search: {
      handler: function () {
        this.getNominees()
      },
      deep: true
    },
    pagination: {
      handler () {
        this.getNominees()
      }
    },
    getNominationsCategories: {
      handler: function (newVal) {
        this.allCategories = clone(newVal)
      },
      deep: true
    }
  },
  methods: {
    ...mapActions([
      'getNomineesList',
      'fetchNomineesListWithWiningStatus',
      'getNominationsCategoriesList',
      'removeNominee',
      'getNominationsCategoriesList',
      'approveNominee',
      'exportNomineesExcel'
    ]),
    async setApprovedStatus (id) {
      await this.approveNominee(id)
      this.isForceRequest = true
      await this.getNominees()
    },
    formatDate (date) {
      return moment(date).format('LLL')
    },
    receiveNomineeCategories (categoriesIds) {
      const nomineeCategories = this.allCategories.filter((category) => {
        return categoriesIds.includes(category.id)
      })
      return nomineeCategories
    },
    checkIfDraftStatus (status) {
      return status === 'Draft'
    },
    getNominees: debounce(async function () {
      this.loading = true
      const { sortBy, descending, page, rowsPerPage } = this.pagination
      let itemsOnPage = equals(rowsPerPage, -1) ? this.totalNomineesItems : rowsPerPage
      const sort = descending ? 'Desc' : 'Asc'
      const name = sortBy ? sortBy[0].toUpperCase() + sortBy.slice(1) : null
      const newPage = page - 1
      const pageCheck = newPage > 0 ? newPage : 0
      const getParams = {
        page: pageCheck,
        itemsOnPage,
        filter: this.search,
        sort,
        name
      }
      try {
        if (!equals(getParams, this.comparedParams) || this.isForceRequest) await this.getNomineesList(getParams)
        this.loading = false
        this.comparedParams = clone(getParams)
        this.closeModal()
      } catch (e) {
        console.error(e)
      }
    }, timeSettings.delayTime),
    showEditModal (item) {
      if (!item.target) {
        this.editableItem = clone(item)
      }
      this.isModalOpen = true
    },
    async saveNominee () {
      try {
        this.isForceRequest = true
        const result = await this.$refs.nomineeForm.saveNomineeItem()
        if (result !== 'modal window stay open') {
          this.editableItem = null
          await this.getNominees()
        }
      } catch (err) {
        this.isForceRequest = false
        throw err
      }
    },
    async removeNomineeItem (id) {
      await this.removeNominee(id)
      await this.getNominees()
    },
    closeModal () {
      if (!this.isModalOpen) return
      this.editableItem = null
      this.isModalOpen = false
      this.isForceRequest = false
    },
    reset () {
      this.pagination.sortBy = null
      this.pagination.descending = false
      this.search = {}
      this.showFilter = false
    },
    async receiveRequiredData () {
      try {
        await Promise.all([
          this.getNominees(),
          this.getNominationsCategoriesList(),
          this.fetchNomineesListWithWiningStatus()
        ])
      } catch (err) {
        throw err
      }
    }
  },
  mounted () {
    this.receiveRequiredData()
  }
}
</script>

<style type="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/Awards/Nominee/index.vue